import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { applyColor }from '~src/style/ColorService';

import {
  classes,
  cssClass
} from '../../style';
import { AuthorizableType } from '../types/AuthorizableType';
import { CapabilitySection, CapabilitySectionsByAuthorizableType } from '../types/CapabilitySections';

export namespace PermissionsGridStyles {
  const actionDialogPrefix = 'ActionDialog';
  const granteeCellCssPrefix = 'GranteeCell';
  const permissionsGridCssPrefix = 'PermissionsGrid';
  const permissionRoleCellCssPrefix = 'PermissionRoleCell';
  const siteRoleCellCssPrefix = 'SiteRoleCell';
  const capabilitiesCellCssPrefix = 'CapabilitiesCell';
  const capabilitiesHeaderCssPrefix = 'CapabilitiesHeader';
  const disabledTextStyleCssPrefix = 'DisabledTextStyle';
  const effectivePermissionsGridEmptyStateCssPrefix = 'EffectivePermissionsGridEmptyState';
  const explicitPermissionsGridEmptyStateCssPrefix = 'ExplicitPermissionsGridEmptyState';

  const ButtonPanelLeftPadding = 7;
  // NOTE: ButtonPanelWidth =
  // 2*(button width 46 + 2*(button padding 24 + button border 1))
  // + 12 middle margin between buttons
  // + 25 to accommodate long translated text (TFSID 1086270)
  // + 60 increased grantee cell width - made longer from original width to better show long group names
  export const IncreasedGranteeCellWidth = 60;
  const ButtonPanelWidth = 229 + IncreasedGranteeCellWidth;
  const ActionMenuWidth = 43;
  const DropdownArrowWidth = 18;
  const NoResultsTextFontSize = 18;
  const NoResultsTextTopPadding = 40;
  /** max number of body rows (i.e. excluding header row) */
  export const MaxRows = 6;
  export const RoleCellWidth = 100;
  export const EmptySpaceMargin = 24;
  export const RoleCellMarginRight = 13;
  export const RoleCellTotalWidth = RoleCellWidth + EmptySpaceMargin + RoleCellMarginRight;
  export const IconHeight = 18;
  export const RowHeight = 32;
  export const GranteeNameWidth = ButtonPanelWidth + ButtonPanelLeftPadding;
  export const GranteeCellWidth = GranteeNameWidth + ActionMenuWidth;
  export const SelectedRowBackgroundColor = applyColor(DesignTokens.color.component.background.header.default.info, '#CDE7F8');
  const DisabledTextColor = applyColor(DesignTokens.color.font.default.disabled, '#BFBFBF');
  const GridBorderBottomColor = '#CBCBCB';
  const GranteeSearchFieldSpace = 4;
  const GranteeSearchFieldWidth = GranteeCellWidth - 25;
  const GranteeSearchFieldPopupWidth = GranteeSearchFieldWidth - 20;
  const GranteeSearchFieldPopupContentWidth = GranteeSearchFieldPopupWidth - 12;
  const GranteeSearchFieldPopupMaxHeight = 120;
  export const GranteeSearchFieldPopupHeight = 113;
  export const GranteeSearchFieldPopupWhitespaceHeight = 14;
  export const GranteeSearchFieldPopupTotalHeight = GranteeSearchFieldSpace + GranteeSearchFieldPopupMaxHeight + GranteeSearchFieldPopupWhitespaceHeight;
  const GranteeSearchResultImgContainerWidth = 24;
  const GranteeSearchResultImgWidth = 14;
  const HoveredRowBackgroundColor = '#F5F5F5';
  export const HoverableGranteeCellClass = 'grantee-cell-hoverable';
  export const GranteeCellStripeClass = 'grantee-cell-stripe';
  const effectivePermissionRowsCursor = 'effective-permission-rows-cursor';
  const editableButtonPanel = 'editable-button-panel';

  const capabilityWidth = 32; // width of individual checkbox
  const capabilityLeftMargin = 11; // whitespace to the left of an individual checkbox that is not the first in its section
  const capabilitySectionLeftMargin = 24; // whitespace to the left of a capability section (except for first section)
  const capabilityCellPadding = 12; // padding on the left and right side of the overall cell
  const capabilityCellWhitespaceEnd = 14; // extra whitespace on the right end of the cell
  const capabilityCellBorder = 1; // whitespace due to border to the left of the cell

  export function capabilityCellWidth(): number {
    const capabilitySections: ReadonlyArray<CapabilitySection> = CapabilitySectionsByAuthorizableType(
      true,
      FeatureFlags.explainDataPermissionEnabled,
      FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled,
      getIsOnlineEnvironment(),
      FeatureFlags.allowNonAdminRefreshExtractsEnabled)[AuthorizableType.WORKBOOK];
    let width = 2 * capabilityCellPadding + capabilityCellWhitespaceEnd + capabilityCellBorder // whitespace that is always there no matter how many capability sections there are
                + (capabilitySections.length - 1) * (capabilitySectionLeftMargin + capabilityWidth) + capabilityWidth; // width + whitespace for the first capability of each section
    capabilitySections.forEach((section: CapabilitySection) => {
      width += (section.capabilities.length - 1) * (capabilityWidth + capabilityLeftMargin); // width of all capabilities + whitespace of the section, excluding the first capability of the section
    });
    return width;
  }

  function getIsOnlineEnvironment(): boolean {
    try {
      return getSessionManagementServiceInstance().isOnlineEnvironment();
    } catch (error) {
      // If session management service is not initialized, default to false (on-premise)
      return false;
    }
  }

  export const DisabledTextStyle = cssClass(disabledTextStyleCssPrefix, '', {
    color: DisabledTextColor,
  });

  export const effectivePermissionsGridEmptyStateStyle = cssClass(effectivePermissionsGridEmptyStateCssPrefix, '', {
    color: applyColor(DesignTokens.color.font.default.secondary, `${CommonStyles.Colors.AccessibleSecondaryTextColor}`),
    fontSize: `${NoResultsTextFontSize}px`,
    paddingTop: `${NoResultsTextTopPadding}px`,
    textAlign: 'center',
  });

  export const explicitPermissionsGridEmptyStateStyle = cssClass(explicitPermissionsGridEmptyStateCssPrefix, '', {
    color: applyColor(DesignTokens.color.font.default.secondary, `${CommonStyles.Colors.AccessibleSecondaryTextColor}`),
    lineHeight: `${RowHeight}px`,
  });

  export const effectivePermissionRowsPointerCursor = cssClass(permissionsGridCssPrefix, effectivePermissionRowsCursor, {
    $nest: {
      '& .tb-react-dg-brow': {
        cursor: 'pointer',
      },
    },
  });

  export type DataGridContainerStyleArgs = {
    /** whether or not all rows are disabled except for the one being edited */
    disabled: boolean;
    /** whether or not the last row is being edited */
    lastRowInEditMode: boolean;
    /** number of grid rows, including header row and any temporarily added rows when editing/adding a permission rule */
    numRows: number;
    /** whether or not the grantee search popup is visible */
    searching: boolean;
  };

  export let memoizedDataGridContainer = _.memoize(DataGridContainer, (args) => { return JSON.stringify(args); });
  export function DataGridContainer(args: DataGridContainerStyleArgs): string {
    const borderBottom = `1px solid ${!args.lastRowInEditMode && args.numRows > 1 ? applyColor(DesignTokens.color.font.default.subtle, GridBorderBottomColor) : 'transparent'}`;
    const gridCellWithButtonPanelSelector = `[role="gridcell"]:has([class*="${editableButtonPanel}"])`;
    const maxRowsIncludingHeaderRow = MaxRows + 1;
    const scrollBarAllowance = 20;
    let height = args.numRows * RowHeight + scrollBarAllowance + 1; // extra 1px because there is a 1px border below the header row
    let maxHeight = maxRowsIncludingHeaderRow * RowHeight + scrollBarAllowance + 1; // extra 1px because there is a 1px border below the header row
    if (args.lastRowInEditMode) {
      maxHeight += RowHeight; // account for the button panel that appears inside the last row
    }
    if (args.searching) { // account for the grantee search results that appears inside the last row
      height += GranteeSearchFieldPopupHeight;
      maxHeight += GranteeSearchFieldPopupHeight;
    }

    return cssClass(permissionsGridCssPrefix, 'container', {
      borderBottom,
      height,
      maxHeight,
      $nest: {
        ...!args.disabled && {
          '& .tb-react-dg-brow:not([aria-selected="true"]):hover': {
            backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, HoveredRowBackgroundColor),
            $nest: {
              [`.${HoverableGranteeCellClass}`]: {
                backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, HoveredRowBackgroundColor),
              },
            },
          },
        },
        '& .tb-react-dg-body': {
          backgroundColor: 'transparent'+' !important', // the extra height behind search results needs to be transparent instead of VPC global white background
          overflowY: args.disabled || args.numRows <= 1 ? 'hidden !important' as any : undefined, // override DataGrid styling to have no vertical scrollbar when the grid is disabled or empty
          // The DataGrid sizes its scrollable content from rows.length * rowOuterHeight, so it doesn't reserve room for the
          // absolutely-positioned button panel (or the search popup) hanging off the last row. Without padding, editing the
          // last row of a scrolled list leaves the panel beyond maxScroll and stranded off-screen.
          ...args.lastRowInEditMode && {
            paddingBottom: `${args.searching ? GranteeSearchFieldPopupTotalHeight : RowHeight}px`,
          },
        },
        '& [aria-colindex="1"]': { // grantee cell column
          minWidth: '242px !important', // grantee cell has to have min-width or else edit button panel looks wrong
          paddingLeft: '0px !important', // grantee cell doesn't have the default cell padding
          paddingRight: '0px !important', // grantee cell doesn't have the default cell padding
        },

        // DataGrid cells normally have the same height (`RowHeight`), but when editing a permission rule,
        // there's a particular grid cell that needs different height because otherwise the button panel and search results
        // would appear cut off since they are not actually inside an overlay.
        [`& ${gridCellWithButtonPanelSelector}`]: {
          backgroundColor: 'transparent'+' !important', // the extra height behind search results needs to be transparent instead of selected color
          height: `${RowHeight + (args.searching ? GranteeSearchFieldPopupTotalHeight : RowHeight)}px !important`,
          // DataGrid draws a horizontal-line border on the bottom of every body cell; on this stretched cell that border
          // ends up drawn well below the row, cutting through the effective-permissions search field below the grid.
          borderBottom: 'none !important' as any,
        },
        ...args.searching && {
          [`& ${gridCellWithButtonPanelSelector} > div`]: {
            height: `${RowHeight + GranteeSearchFieldPopupTotalHeight}px !important`,
          },
        },
      }
    });
  }

  export const ActionDialogText = cssClass(actionDialogPrefix, 'body', {
    color: applyColor(DesignTokens.color.font.default.default, '#333333'),
  });


  export const GranteeInfoCellContainer = cssClass(granteeCellCssPrefix, 'info-container', {
    display: 'flex',
    minWidth: '204px',
    overflow: 'hidden',
  });

  export const GranteeCellImgContainer = cssClass(granteeCellCssPrefix, 'img-container', {
    alignItems: 'center',
    borderLeft: '3px solid transparent',
    display: 'inline-flex',
    height: `${RowHeight}px`,
    width: `${RowHeight}px`,
    $nest: {
      [`&.${GranteeCellStripeClass}`]: {
        borderColor: CommonStyles.Colors.Grey102,
      },
    },
  });

  export const GranteeCellImg = cssClass(granteeCellCssPrefix, 'img-container', {
    margin: 'auto'
  });

  export const DataGridCellOverrideRemovePadding: React.CSSProperties = {
    paddingRight: 0,
  };

  export const DataGridPermissionRulesCellOverrideRemovePadding: React.CSSProperties = {
    paddingRight: 1,
    paddingLeft: 1,
    paddingTop: 1,
  };

  const GranteeCellTextBase = cssClass(granteeCellCssPrefix, 'text', {
    marginLeft: '8px',
    marginRight: '24px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: `${GranteeNameWidth}px`,
  });

  export const GranteeCellTextColor = cssClass(granteeCellCssPrefix, 'text-color', {
    color: applyColor(DesignTokens.color.font.default.default, CommonStyles.Colors.Grey51),
  });

  export const GranteeCellTextColorDisabled = cssClass(granteeCellCssPrefix, 'text-color-disabled', {
    color: DisabledTextColor,
  });

  export const GranteeCellText = classes(GranteeCellTextBase, GranteeCellTextColor);
  export const GranteeCellTextDisabled = classes(GranteeCellTextBase, GranteeCellTextColorDisabled);

  export const GranteeCellContainerSelected = cssClass(granteeCellCssPrefix, 'container-selected', {
    backgroundColor: SelectedRowBackgroundColor,
  });

  export const GranteeCellContainerExplicit = cssClass(granteeCellCssPrefix, 'container-explicit', {
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, CommonStyles.Colors.Grey250),
    borderRight: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, CommonStyles.Colors.Grey235)}`,
  });

  export const GranteeCellContainerExplicitSelected = cssClass(granteeCellCssPrefix, 'container-explicit-selected', {
    backgroundColor: SelectedRowBackgroundColor,
    borderRight: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, CommonStyles.Colors.Grey235)}`,
  });

  const EditableGranteeCellContainerBase = cssClass(granteeCellCssPrefix, 'editable-container', {
    alignItems: 'center',
    borderRight: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, CommonStyles.Colors.Grey235)}`,
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    justifyContent: 'space-between',
    $nest: {
      'button[data-tb-test-id="action-menu-Button"]': {
        $nest: {
          '[class^="spaces-explorer_explorer-dropdown-button"]': {

            backgroundColor: 'transparent'+' !important',
          }
        },
        backgroundColor: 'transparent'+' !important',
      },
    },
  });

  const EditableGranteeCellContainerColors = cssClass(granteeCellCssPrefix, 'editable-container-colors', {
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, CommonStyles.Colors.Grey250),
    borderBottom: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, CommonStyles.Colors.Grey235)}`,
  });

  const EditableGranteeCellContainerColorsSelected = cssClass(granteeCellCssPrefix, 'editable-container-colors-selected', {
    backgroundColor: SelectedRowBackgroundColor,
    borderBottom: `1px solid ${SelectedRowBackgroundColor}`,
  });

  export const EditableGranteeCellContainer = classes(EditableGranteeCellContainerBase, EditableGranteeCellContainerColors);
  export const EditableGranteeCellContainerSelected = classes(EditableGranteeCellContainerBase, EditableGranteeCellContainerColorsSelected);

  export const EditableGranteeCellSearchField = cssClass(granteeCellCssPrefix, 'editable-search-field', {
    position: 'relative',
    height: `${RowHeight}px`,
    width: `${GranteeSearchFieldWidth}px`,
    $nest: {
      '& input': { backgroundColor: `${applyColor(DesignTokens.color.elevation.surface.default.default, CommonStyles.Colors.White)} !important`},
      '& [role="listbox"]': {
        backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, CommonStyles.Colors.White),
        border: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L6)}`,
        margin: '1px 3px',
        maxHeight: `${GranteeSearchFieldPopupHeight}px`,
        position: 'absolute',
        top: '100%',
        width: `${GranteeSearchFieldPopupWidth}px`,
        zIndex: 2 // allow popup to show up above button panel
      },
      '&:not(.showPopup) [role="listbox"]': {
        display: 'none',
      },
      '& label': {
        display: 'none',
      },
    }
  });

  export const EditableGranteeCellSearchFieldContainer = cssClass(granteeCellCssPrefix, 'editable-search-field-container', {
    paddingLeft: '4px',
    paddingRight: '21px',
    height: `${RowHeight}px`
  });

  export const EditableGranteeCellSearchResultContainer = cssClass(granteeCellCssPrefix, 'editable-search-result-container', {
    overflow: 'visible',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: `${GranteeSearchFieldPopupContentWidth}px`,
  });

  export const EditableGranteeCellSearchResultImg = cssClass(granteeCellCssPrefix, 'editable-search-result-img', {
    height: `${GranteeSearchResultImgWidth}px`,
    width: `${GranteeSearchResultImgWidth}px`,
  });

  export const EditableGranteeCellSearchResultImgContainer = cssClass(granteeCellCssPrefix, 'editable-search-result-img-container', {
    alignItems: 'center',
    display: 'inline-flex',
    height: `${GranteeSearchResultImgContainerWidth}px`,
    verticalAlign: 'middle',
    width: `${GranteeSearchResultImgContainerWidth}px`,
  });

  export const EditableGranteeCellSubContainer = cssClass(granteeCellCssPrefix, 'editable-sub-container', {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    overflow: 'hidden',
    width: '211px',
  });

  export const EditableGranteeCellButtonPanel = cssClass(granteeCellCssPrefix, editableButtonPanel, {
    backgroundColor: SelectedRowBackgroundColor,
    justifyContent: 'left',
    marginTop: '-2px',
    paddingLeft: '7px',
    position: 'absolute',
    width: `${ButtonPanelWidth + ActionMenuWidth - 2}px`,
  });

  export const EditableGranteeCellButton = cssClass(granteeCellCssPrefix, 'editable-button-panel-button', {
    lineHeight: '18px',
    $nest: {
      'img': {
        verticalAlign: 'middle', // loading spinner appears towards top of button without this
      },
    },
  });

  const EditableGranteeCellCancelButtonMargin = cssClass(granteeCellCssPrefix, 'editable-button-panel-cancel', {
    marginRight: '12px',
  });

  export const EditableGranteeCellCancelButton = classes(EditableGranteeCellCancelButtonMargin, EditableGranteeCellButton);

  export const SelectedPermissionRoleCellContainer = cssClass(permissionRoleCellCssPrefix, 'selected-container', {
    $nest: {
      'button:hover:enabled': {
        backgroundColor: SelectedRowBackgroundColor,
      },
    },
  });

  export const NotAllowedPermissionCellContainer = cssClass('container', 'not-allowed', {
    $nest: {
      ':hover': {
        cursor: 'not-allowed !important',
      },
    },
  });

  export const PermissionRoleCellContainer = cssClass(permissionRoleCellCssPrefix, 'container', {
    $nest: {
      '&>div': {
        display: 'initial !important',
        $nest: {
          'button[data-tb-test-id="permissions-grid-permission-role-Button"]': {
            $nest: {
              '[class^="spaces-explorer_explorer-dropdown-button"]': {
                backgroundColor: 'transparent'+' !important',
              },
            },
            backgroundColor: 'transparent'+' !important',
          },
        },
      },
      'button:hover:enabled': {
        cursor: 'pointer'
      },
    },
  });

  export const PermissionRoleCellSubContainerBase = cssClass(permissionRoleCellCssPrefix, 'subcontainer-base', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexGrow: 1,
  });

  const PermissionRoleCellSubContainerSelectedColor = cssClass(permissionRoleCellCssPrefix, 'subcontainer-selected', {
    backgroundColor: SelectedRowBackgroundColor,
  });

  export const PermissionsRoleCellSubContainerSelected = classes(PermissionRoleCellSubContainerBase, PermissionRoleCellSubContainerSelectedColor);

  export const PermissionRoleCellText = cssClass(siteRoleCellCssPrefix, 'text', {
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: `${RoleCellWidth - DropdownArrowWidth}px`,
  });

  export const SiteRoleCellText = cssClass(siteRoleCellCssPrefix, 'text', {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: `${RoleCellWidth}px`,
  });

  export const SiteRoleTextDisabled = classes(DisabledTextStyle, SiteRoleCellText);

  export const CapabilitiesCellSection = cssClass(capabilitiesCellCssPrefix, 'section', {
    marginLeft: `${capabilitySectionLeftMargin}px`,
  });

  export const CapabilitiesCellCapabilityContainerBase = cssClass(capabilitiesCellCssPrefix, 'capability-container', {
    width: `${capabilityWidth}px`,
    $nest: {
      '&>div': {
        display: 'inline-flex',
        height: '14px',
        verticalAlign: 'middle',
      }
    }
  });

  const CapabilitiesCellCapabilityContainerLeftMargin = cssClass(capabilitiesCellCssPrefix, 'capability-container-left-margin', {
    marginLeft: `${capabilityLeftMargin}px`,
  });

  export const CapabilitiesCellCapabilityContainerWithLeftMargin = classes(CapabilitiesCellCapabilityContainerBase, CapabilitiesCellCapabilityContainerLeftMargin);

  export const CapabilitiesCellCapability = cssClass(capabilitiesCellCssPrefix, 'capability', {
    display: 'inline-flex',
    width: `${capabilityWidth}px`,
  });

  export const EditableCapabilitiesCellCapability = cssClass(capabilitiesCellCssPrefix, 'editable-capability', {
    display: 'inline-flex',
    width: `${capabilityWidth}px`,
    border: `1px solid ${applyColor(DesignTokens.color.component.border.neutral.default.default, CommonStyles.Colors.Grey51)}`,
    boxSizing: 'border-box',
  });

  export const HoverableCapabilitiesCellContainer = cssClass(capabilitiesCellCssPrefix, 'hoverable-capability', {
    $nest: {
      '&:hover': {
        border: `1px solid ${applyColor(DesignTokens.color.component.border.neutral.default.default, CommonStyles.Colors.Grey51)}`,
        boxSizing: 'border-box',
      },
    },
  });

  export const CapabilitiesCellImg = cssClass(capabilitiesCellCssPrefix, 'img', {
    height: '8px',
    margin: 'auto',
  });

  export const CapabilitiesCellImgMixed = cssClass(capabilitiesCellCssPrefix, 'img-mixed', {
    height: '2px',
    margin: 'auto',
  });

  export const CapabilitiesCellDisabled = cssClass(capabilitiesCellCssPrefix, 'disabled', {
    opacity: 0.5
  });

  export const CapabilitiesCellAllowedCapability = cssClass(capabilitiesCellCssPrefix, 'allowed', {
    backgroundColor: applyColor(DesignTokens.color.feedback.default.success_subtle, TabStyles.Colors.GoDark),
  });

  export const CapabilitiesCellDeniedCapability = cssClass(capabilitiesCellCssPrefix, 'denied', {
    backgroundColor: applyColor(DesignTokens.color.feedback.default.error, '#E51F25'),
  });

  export const CapabilitiesCellMixedCapability = cssClass(capabilitiesCellCssPrefix, 'mixed', {
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.dark, CommonStyles.Colors.Grey203),
  });

  export const CapabilitiesCellUnspecifiedCapability = cssClass(capabilitiesCellCssPrefix, 'unspecified', {
    backgroundColor: applyColor(DesignTokens.color.component.background.neutral.default.active, CommonStyles.Colors.Grey230),
  });

  export const EditableCapabilitiesCellUnspecifiedCapability = cssClass(capabilitiesCellCssPrefix, 'editable-unspecified', {
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, CommonStyles.Colors.White),
  });

  export const CapabilitiesHeader = cssClass(capabilitiesHeaderCssPrefix, '', {
    alignItems: 'center',
    display: 'inline-flex',
    height: `${RowHeight}px`,
  });

  export const CapabilitiesHeaderSection = cssClass(capabilitiesHeaderCssPrefix, 'section', {
    display: 'inline-flex',
  });

  export const CapabilitiesHeaderSectionLeftDivider = cssClass(capabilitiesHeaderCssPrefix, 'section-left-divider', {
    borderLeft: `1px solid ` + applyColor(DesignTokens.color.elevation.border.default.subtle, '#D4D4D4'),
    marginLeft: '15px',
    paddingLeft: '12px',
  });

  export const CapabilitiesHeaderSectionWithLeftDivider = classes(CapabilitiesHeaderSection, CapabilitiesHeaderSectionLeftDivider);

  export const CapabilitiesHeaderIconContainerBase = cssClass(capabilitiesHeaderCssPrefix, 'icon-container', {
    display: 'inline-flex',
  });

  export const CapabilitiesHeaderIconContainerNormalLeftMargin = cssClass(capabilitiesHeaderCssPrefix, 'icon-container-normal-left-margin', {
    marginLeft: '19px',
  });

  export const CapabilitiesHeaderIconContainerSmallLeftMargin = cssClass(capabilitiesHeaderCssPrefix, 'icon-container-small-left-margin', {
    marginLeft: '4px',
  });

  export const CapabilitiesHeaderCapabilityIcon = cssClass(capabilitiesHeaderCssPrefix, 'capability-icon', {
    height: `${IconHeight}px`,
    width: `${EmptySpaceMargin}px`,
  });

  export const CapabilitiesHeaderIconContainerFirst = classes(CapabilitiesHeaderIconContainerBase, CapabilitiesHeaderIconContainerSmallLeftMargin);
  export const CapabilitiesHeaderIconContainer = classes(CapabilitiesHeaderIconContainerBase, CapabilitiesHeaderIconContainerNormalLeftMargin);
}
